import { dbPut, dbGet } from "@p4b/utils-db";

export interface ReconnectingEventSourceConfiguration extends EventSourceInit {
    retryTime?: number;
    headers?: Record<string, string>;
}

export type EventSourceListener = (event: MessageEvent<string>) => void;

export class ReconnectingEventSource {
    public static readonly CONNECTING = 0;
    public static readonly OPEN = 1;
    public static readonly CLOSED = 2;

    private url: URL;
    private configuration: EventSourceInit = {};
    private headers?: Record<string, string>;
    private eventSource?: EventSource;
    private abortController?: AbortController;
    private timer?: number;
    private listeners = new Map<keyof EventSourceEventMap, EventSourceListener[]>();
    private esReadyState: number = ReconnectingEventSource.CONNECTING;
    private retryTime = 3000;

    get readyState(): number {
        return this.esReadyState;
    }

    constructor(url: URL, configuration?: ReconnectingEventSourceConfiguration) {
        this.url = url;
        if (configuration) {
            const {retryTime, headers, ...esconf} = configuration;
            if (retryTime) {
                this.retryTime = retryTime;
            }
            if (headers) {
                this.headers = headers;
            }
            this.configuration = esconf;
        }
        window.addEventListener('offline', this.handleOffline);
        window.addEventListener('online', this.handleOnline);
        this.connect();
    }

    public destroy(): void {
        window.removeEventListener('offline', this.handleOffline);
        window.removeEventListener('online', this.handleOnline);
        this.close();
    }

    private readonly handleOnline = () => {
        this.connect();
    }

    private readonly handleOffline = () => {
        this.close();
    }

    private async connect() {
        if (!navigator.onLine) {
            return;
        }

        const lastEventId = await dbGet('users', `${this.url}_lastEventId`);

        if (this.headers) {
            await this.connectWithFetch(lastEventId);
        } else {
            await this.connectWithEventSource(lastEventId);
        }
    }

    private async connectWithEventSource(lastEventId: unknown) {
        const url = new URL(String(this.url));
        if (typeof lastEventId === 'string') {
            console.debug(`SSE {url=${url} lastEventId=${lastEventId}}`);
            url.searchParams.append('lastEventId', lastEventId);
        }
        this.eventSource = new EventSource(String(url), this.configuration);
        this.eventSource.onopen = this.handleOpen;
        this.eventSource.onerror = this.handleError;
        for (const type of Array.from(this.listeners.keys())) {
            this.eventSource.addEventListener(type, this.handleEvent);
        }
    }

    private async connectWithFetch(lastEventId: unknown) {
        this.abortController = new AbortController();

        const headers: Record<string, string> = {
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
            ...this.headers
        };

        if (typeof lastEventId === 'string') {
            headers['Last-Event-ID'] = lastEventId;
            console.debug(`SSE {url=${this.url} lastEventId=${lastEventId}}`);
        }

        try {
            const response = await fetch(String(this.url), {
                headers,
                signal: this.abortController.signal,
                cache: 'no-store',
                credentials: this.configuration.withCredentials ? 'include' : 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            if (!response.body) {
                throw new Error('No response body');
            }

            this.handleOpen(new Event('open'));

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let currentEvent: {type: string, data: string, id: string} = {type: 'message', data: '', id: ''};

            while (true) {
                const {value, done} = await reader.read();

                if (done) {
                    this.handleError(new Event('error'));
                    break;
                }

                buffer += decoder.decode(value, {stream: true});
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data:')) {
                        currentEvent.data += line.substring(5).trimStart() + '\n';
                    } else if (line.startsWith('event:')) {
                        currentEvent.type = line.substring(6).trimStart();
                    } else if (line.startsWith('id:')) {
                        currentEvent.id = line.substring(3).trimStart();
                    } else if (line === '') {
                        if (currentEvent.data) {
                            currentEvent.data = currentEvent.data.slice(0, -1);
                            console.debug('SSE message received:', {type: currentEvent.type, id: currentEvent.id, data: currentEvent.data});
                            const messageEvent = new MessageEvent(currentEvent.type, {
                                data: currentEvent.data,
                                lastEventId: currentEvent.id,
                                origin: this.url.origin
                            });
                            await this.handleEvent(messageEvent);
                        }
                        currentEvent = {type: 'message', data: '', id: ''};
                    }
                }
            }
        } catch (err) {
            if (err instanceof Error && err.name !== 'AbortError') {
                console.error('SSE fetch error:', err);
                this.handleError(new Event('error'));
            }
        }
    }

    private readonly handleConnect = () => {
        this.connect();
    }

    private readonly handleOpen = (event: Event) => {
        if (this.esReadyState === ReconnectingEventSource.CONNECTING) {
            this.esReadyState = ReconnectingEventSource.OPEN;
            this.onopen(event);
        }
    }

    private readonly handleError = (event: Event) => {
        if (this.esReadyState === ReconnectingEventSource.OPEN) {
            this.esReadyState = ReconnectingEventSource.CONNECTING;
            this.onerror(event);
        }

        if (this.eventSource) {
            if (this.eventSource.readyState === ReconnectingEventSource.CLOSED) {
                this.eventSource.close();
                this.eventSource = undefined;
            }
        }

        if (this.abortController) {
            this.abortController.abort();
            this.abortController = undefined;
        }

        if (!this.timer && this.esReadyState !== ReconnectingEventSource.CLOSED) {
            this.timer = window.setTimeout(this.handleConnect, this.retryTime);
        }
    };

    private readonly handleEvent = async (event: Event|MessageEvent<keyof EventSourceEventMap>) => {
        if (event instanceof MessageEvent) {
            console.debug('SSE message received:', {type: event.type, id: event.lastEventId, data: event.data});
            if (event.lastEventId) {
                await dbPut('users', event.lastEventId, `${this.url}_lastEventId`);
            }

            let listeners = this.listeners.get(event.type as keyof EventSourceEventMap);
            if (listeners) {
                listeners = listeners.slice();
                for (let i = 0; i < listeners.length; ++i) {
                    listeners[i](event);
                }
            }
            if (event.type === 'message') {
                this.onmessage(event);
            }
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public onopen: (event: Event) => void = () => {};

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public onerror: (event: Event) => void = () => {};

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public onmessage: (event: MessageEvent<string>) => void = () => {};

    public close(): void{
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = undefined;
        }

        if (this.eventSource) {
            for (const type of Array.from(this.listeners.keys())) {
                this.eventSource.removeEventListener(type, this.handleEvent);
            }
            this.eventSource.close();
            this.eventSource = undefined;
        }

        if (this.abortController) {
            this.abortController.abort();
            this.abortController = undefined;
        }

        this.esReadyState = ReconnectingEventSource.CLOSED;
    }

    public addEventListener(type: keyof EventSourceEventMap, callback: EventSourceListener): void {
        let listeners = this.listeners.get(type);
        if (!listeners) {
            listeners = [];
            this.listeners.set(type, listeners);
            if (this.eventSource) {
                this.eventSource.addEventListener(type, this.handleEvent);
            }
        }
        for (let i = 0; i < listeners.length; ++i) {
            if (listeners[i] === callback) {
                return;
            }
        }
        listeners.push(callback);
    }

    public removeEventListener(type: keyof EventSourceEventMap, callback:EventSourceListener): void {
        const listeners = this.listeners.get(type);
        if (!listeners) {
            return;
        }
        for(let i = 0; i < listeners.length; ++i) {
            if (listeners[i] === callback) {
                listeners.splice(i, 1);
                break;
            }
        }
        if (listeners.length === 0) {
            this.listeners.delete(type)
            if (this.eventSource) {
                this.eventSource.removeEventListener(type, this.handleEvent);
            }
        }
    }
}
